import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '../base/Button';
import rmcLogo from '../../assets/img/home/rjy.png';

interface HeaderProps {
  onLanguageToggle: (lang: string) => void;
  currentLanguage: string;
}

export default function Header({ onLanguageToggle, currentLanguage }: HeaderProps) {
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isThemeDropdownOpen, setIsThemeDropdownOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isResourcesDropdownOpen, setIsResourcesDropdownOpen] = useState(false);
  const [isDashboardsDropdownOpen, setIsDashboardsDropdownOpen] = useState(false);
  const [isTourismDropdownOpen, setIsTourismDropdownOpen] = useState(false);
  const [fontSize, setFontSize] = useState(() => localStorage.getItem('fontSize') || 'normal');
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'default');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isLoginDropdownOpen, setIsLoginDropdownOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const [citizenId, setCitizenId] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0, width: 0 });
  const [loginDropdownPosition, setLoginDropdownPosition] = useState({ top: 0, right: 0, width: 0 });
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const loginButtonRef = useRef<HTMLButtonElement>(null);
  const profileButtonMobileRef = useRef<HTMLButtonElement>(null);
  const loginButtonMobileRef = useRef<HTMLButtonElement>(null);
  
  const location = useLocation();
  const navigate = useNavigate();

  // Check login status
  useEffect(() => {
    const checkLoginStatus = () => {
      const citizenName = localStorage.getItem('citizenName');
      const token = 
        localStorage.getItem('token') || 
        localStorage.getItem('x-access-token') || 
        sessionStorage.getItem('token') || 
        sessionStorage.getItem('x-access-token');
      
      if (citizenName || token) {
        setIsLoggedIn(true);
        setUserName(citizenName || 'User');
        // Generate or get citizen ID - you can modify this based on your actual data structure
        const storedCitizenId = localStorage.getItem('citizenId') || `RMC${new Date().getFullYear()}${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`;
        if (!localStorage.getItem('citizenId')) {
          localStorage.setItem('citizenId', storedCitizenId);
        }
        setCitizenId(storedCitizenId);
      } else {
        setIsLoggedIn(false);
        setUserName('');
        setCitizenId('');
      }
    };

    checkLoginStatus();
    // Listen for storage changes (when user logs in/out in another tab)
    window.addEventListener('storage', checkLoginStatus);
    // Check periodically in case localStorage is updated
    const interval = setInterval(checkLoginStatus, 1000);

    return () => {
      window.removeEventListener('storage', checkLoginStatus);
      clearInterval(interval);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'te', name: 'తెలుగు', flag: '🇮🇳' },
    // { code: 'hi', name: 'हिंदी', flag: '🇮🇳' }
  ];

  const themes = [
    { id: 'default', name: 'Default', preview: 'bg-blue-600' },
    { id: 'high-contrast', name: 'High Contrast', preview: 'bg-black' },
    { id: 'night', name: 'Night Mode', preview: 'bg-gray-800' },
    { id: 'sepia', name: 'Dyslexia Friendly', preview: 'bg-amber-600' }
  ];

  // Header is now always sticky - no scroll behavior needed

  // Calculate dropdown position when it opens (use whichever button is currently visible)
  useEffect(() => {
    if (isProfileDropdownOpen) {
      const btn =
        profileButtonRef.current && profileButtonRef.current.offsetParent !== null
          ? profileButtonRef.current
          : profileButtonMobileRef.current;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
          width: rect.width
        });
      }
    }
  }, [isProfileDropdownOpen]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isProfileDropdownOpen && !target.closest('.profile-dropdown-container')) {
        setIsProfileDropdownOpen(false);
      }
    };

    if (isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isProfileDropdownOpen]);

  // Calculate login dropdown position when it opens (use whichever button is currently visible)
  useEffect(() => {
    if (isLoginDropdownOpen) {
      const btn =
        loginButtonRef.current && loginButtonRef.current.offsetParent !== null
          ? loginButtonRef.current
          : loginButtonMobileRef.current;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        setLoginDropdownPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
          width: rect.width
        });
      }
    }
  }, [isLoginDropdownOpen]);

  // Close login dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isLoginDropdownOpen && !target.closest('.login-dropdown-container')) {
        setIsLoginDropdownOpen(false);
      }
    };

    if (isLoginDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isLoginDropdownOpen]);

  // Close language and theme dropdowns when clicking outside (keep click for these)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside language/theme dropdowns
      const isClickInDropdown = target.closest('[class*="language"]') || 
                                 target.closest('[class*="theme"]') ||
                                 target.closest('button[class*="language"]') ||
                                 target.closest('button[class*="theme"]');
      
      if (!isClickInDropdown) {
        setIsLanguageDropdownOpen(false);
        setIsThemeDropdownOpen(false);
      }
    };

    if (isLanguageDropdownOpen || isThemeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isLanguageDropdownOpen, isThemeDropdownOpen]);

  // Apply font size (mobile <640px uses smaller defaults for tighter layout)
  useEffect(() => {
    const root = document.documentElement;
    const applyFontSize = () => {
      const isMobile = window.matchMedia('(max-width: 639px)').matches;
      const sizes = isMobile
        ? { small: '12px', normal: '14px', large: '16px' }
        : { small: '14px', normal: '16px', large: '18px' };
      root.style.fontSize = sizes[fontSize as 'small' | 'normal' | 'large'] || sizes.normal;
    };
    applyFontSize();
    window.addEventListener('resize', applyFontSize);
    localStorage.setItem('fontSize', fontSize);
    return () => window.removeEventListener('resize', applyFontSize);
  }, [fontSize]);

  // Apply theme
  useEffect(() => {
    const body = document.body;
    body.className = body.className.replace(/theme-\w+/g, '');
    body.classList.add(`theme-${theme}`);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const handleCitizenLogin = () => {
    setIsTransitioning(true);
    
    // Page transition effect
    setTimeout(() => {
      navigate('/citizen-login');
    }, 700);
  };

  const handleLogout = () => {
    // Clear all user data
    localStorage.removeItem('citizenName');
    localStorage.removeItem('citizenId');
    localStorage.removeItem('citizenMobile');
    localStorage.removeItem('token');
    localStorage.removeItem('x-access-token');
    localStorage.removeItem('ctz_userdata');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('x-access-token');
    
    // Update state
    setIsLoggedIn(false);
    setUserName('');
    setCitizenId('');
    setIsProfileDropdownOpen(false);
    
    // Dispatch custom event for logout so other components can react
    window.dispatchEvent(new CustomEvent('user-logout'));
    
    // Redirect to home
    navigate('/');
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setIsProfileDropdownOpen(false);
  };

  const aboutMenuItems = [
    {
      titleEn: 'History',
      titleTe: 'చరిత్ర',
      path: '/about/history/',
      icon: 'ri-history-line'
    },
    {
      titleEn: 'Prominent People',
      titleTe: 'ప్రముఖ వ్యక్తులు',
      path: '/about/prominent-people/',
      icon: 'ri-user-star-line'
    },
    {
      titleEn: 'Departments',
      titleTe: 'శాఖలు',
      path: '/about/departments/',
      icon: 'ri-building-line'
    },
    {
      titleEn: 'Parks',
      titleTe: 'పార్క్‌లు',
      path: '/about/parks',
      icon: 'ri-leaf-line'
    },
    {
      titleEn: 'Sports',
      titleTe: 'క్రీడలు',
      path: '/about/sports',
      icon: 'ri-football-line'
    },
    {
      titleEn: 'RMC Profile',
      titleTe: 'RMC ప్రొఫైల్',
      path: '/about/history/rajahmundry_profile',
      icon: 'ri-profile-line'
    },
    {
      titleEn: 'Ward Profile',
      titleTe: 'వార్డ్ ప్రొఫైల్',
      path: '/about/history/rajahmundry_ward_profile',
      icon: 'ri-map-pin-line'
    },
    // Sachivalayam option hidden
    // {
    //   titleEn: 'Sachivalayam',
    //   titleTe: 'సచివాలయం',
    //   path: '/about/history/sachavalayam_list',
    //   icon: 'ri-government-line'
    // },
    {
      titleEn: 'Key Contacts',
      titleTe: 'ముఖ్య సంప్రదింపులు',
      path: '/about/history/rajahmundry_whoswho',
      icon: 'ri-phone-line'
    },
    // {
    //   titleEn: 'Transportation',
    //   titleTe: 'రవాణా',
    //   path: '/about/history/rajahmundry_transportation',
    //   icon: 'ri-road-map-line'
    // },
    {
      titleEn: 'Gallery',
      titleTe: 'గ్యాలరీ',
      path: '/about/history/rajahmundry_gallery',
      icon: 'ri-image-line'
    },
    {
      titleEn: 'City Info',
      titleTe: 'నగర సమాచారం',
      path: '/about/info',
      icon: 'ri-information-line'
    },
    {
      titleEn: 'Swarna Wards',
      titleTe: 'స్వర్ణ వార్డులు',
      path: '/gis-map',
      icon: 'ri-map-pin-2-line'
    }
  ];

  const resourcesMenuItems = [
    {
      titleEn: 'Tenders',
      titleTe: 'టెండర్లు',
      path: '/resources/tenders',
      icon: 'ri-file-list-3-line'
    },
    {
      titleEn: 'Acts & By Laws',
      titleTe: 'చట్టాలు మరియు నియమాలు',
      path: '/notice/act_bylaws',
      icon: 'ri-book-open-line'
    },
    {
      titleEn: 'Important Links',
      titleTe: 'ముఖ్యమైన లింకులు',
      path: '/resources/important-links',
      icon: 'ri-links-line'
    },
    {
      titleEn: 'Press Releases',
      titleTe: 'పత్రికా ప్రకటనలు',
      path: '/resources/press-release-gallery',
      icon: 'ri-newspaper-line'
    },
    {
      titleEn: 'Events',
      titleTe: 'కార్యక్రమాలు',
      path: '/resources/events_list',
      icon: 'ri-calendar-event-line'
    }
  ];

  const dashboardMenuItems = [
    {
      titleEn: 'Grievance Dashboards',
      titleTe: 'ఫిర్యాదు డాష్‌బోర్డ్',
      path: '/dashboards/grievance',
      icon: 'ri-feedback-line'
    },
    {
      titleEn: 'City Sanitation',
      titleTe: 'సిటీ సాంద్రీకరణ',
      path: '/dashboards/owms',
      icon: 'ri-drop-line'
    },
    {
      titleEn: 'Street Vendors',
      titleTe: 'స్ట్రీట్ వెండర్స్',
      path: '/dashboards/strtvendors',
      icon: 'ri-shopping-cart-line'
    }
  ];

  const tourismMenuItems = [
    {
      titleEn: 'What to See',
      titleTe: 'చూడవలసినవి',
      path: '/tourism/around_rajahmundry_getaways',
      icon: 'ri-eye-line'
    },
    {
      titleEn: 'What to Eat',
      titleTe: 'తినవలసినవి',
      path: '/tourism/food_in_rajahmundry',
      icon: 'ri-restaurant-line'
    },
    {
      titleEn: 'How to Reach',
      titleTe: 'ఎలా చేరుకోవాలి',
      path: '/about/rajahmundry_transportation',
      icon: 'ri-road-map-line'
    },
    {
      titleEn: 'Prominent Places',
      titleTe: 'ప్రముఖ ప్రదేశాలు',
      path: '/tourism/prominent_places_rajahmundry',
      icon: 'ri-map-pin-line'
    },
    {
      titleEn: 'Religions',
      titleTe: 'మతాలు',
      path: '/tourism/religious_tourism_rajahmundry',
      icon: 'ri-hand-heart-line'
    }
  ];

  return (
    <>
      <style>{`
        /* Remove blue focus borders from dropdown menu items */
        header button:focus,
        header a:focus,
        header button:focus-visible,
        header a:focus-visible {
          outline: none !important;
          box-shadow: none !important;
          ring: none !important;
        }
        header button:focus-within,
        header a:focus-within {
          outline: none !important;
          box-shadow: none !important;
        }
      `}</style>
      {/* Page Transition Overlay */}
      {isTransitioning && (
        <div className="fixed inset-0 z-[9999] pointer-events-none">
          <div 
            className="absolute inset-0 bg-white transition-opacity duration-700 ease-out"
            style={{ opacity: 1 }}
          />
        </div>
      )}

      <header className={`bg-white shadow-lg relative z-[9999] transition-all duration-300 sticky top-0 ${isTransitioning ? 'transform scale-95 opacity-0 transition-all duration-700' : ''}`}>
        {/* Top Utility Bar */}
        <div className="bg-gradient-to-r from-orange-600 via-amber-500 to-orange-600 text-white h-8 lg:h-10">
          <div className="max-w-7xl mx-auto px-3 lg:px-4 flex justify-between items-center h-full text-sm">
            {/* Social Media Links */}
            <div className="flex items-center space-x-3 lg:space-x-4">
              {[
                { icon: 'ri-facebook-fill', href: 'https://www.facebook.com/officeRMC/', delay: '0s' },
                { icon: 'ri-twitter-x-fill', href: 'https://x.com/RMCoffice', delay: '0.1s' },
                { icon: 'ri-instagram-line', href: 'https://www.instagram.com/rmcoffice/', delay: '0.2s' },
                { icon: 'ri-whatsapp-fill', href: 'https://api.whatsapp.com/send/?phone=9494060060&text&type=phone_number&app_absent=0', delay: '0.3s' }
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/50 hover:text-white hover:scale-110 transition-all duration-300 cursor-pointer"
                  style={{ animationDelay: social.delay }}
                >
                  <i className={`${social.icon} text-base lg:text-lg text-white`}></i>
                </a>
              ))}
            </div>

            {/* Emergency Services - RMC Helpline shows from tablet; rest on desktop only */}
            <div className="hidden md:flex items-center space-x-3 text-sm text-white font-medium">
              <span className="animate-pulse hidden lg:inline">
                <span className="text-black font-bold">Emergency:</span> 108
              </span>
              <span className="text-white/50 hidden lg:inline">|</span>
              <span className="hidden lg:inline"><span className="text-black font-semibold">Fire:</span> 101</span>
              <span className="text-white/50 hidden lg:inline">|</span>
              <span className="hidden lg:inline"><span className="text-black font-semibold">Police:</span> 100</span>
              <span className="text-white/50 hidden lg:inline">|</span>
              <span><span className="text-black font-semibold">RMC Helpline:</span> 9494-060-060</span>
            </div>

            {/* Utility Controls */}
            <div className="flex items-center space-x-2 lg:space-x-4">
              {/* Language Switcher */}
              <div className="relative">
                <button
                  onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                  className="flex items-center space-x-1 hover:bg-white/10 px-2 py-1 rounded transition-colors cursor-pointer"
                >
                  <i className="ri-global-line"></i>
                  <span>{languages.find(l => l.code === currentLanguage)?.name}</span>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </button>
                
                {isLanguageDropdownOpen && (
                  <div className="absolute top-full right-0 mt-1 w-40 bg-white rounded-lg shadow-xl border z-50 animate-in slide-in-from-top-2 duration-300">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          onLanguageToggle(lang.code);
                          setIsLanguageDropdownOpen(false);
                        }}
                        className={`w-full flex items-center px-3 py-2 text-gray-700 hover:bg-orange-50 transition-colors cursor-pointer focus:outline-none focus:ring-0 ${
                          currentLanguage === lang.code ? 'bg-orange-100 text-orange-600' : ''
                        }`}
                      >
                        <span className="mr-2">{lang.flag}</span>
                        <span className="text-sm">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Font Size Controller - Hidden on mobile & tablet, shown on desktop */}
              <div className="hidden lg:flex items-center space-x-1">
                <i className="ri-font-size-2 mr-1"></i>
                {['small', 'normal', 'large'].map((size, index) => (
                  <button
                    key={size}
                    onClick={() => setFontSize(size)}
                    className={`px-2 py-1 rounded text-xs transition-all duration-300 cursor-pointer ${
                      fontSize === size
                        ? 'bg-yellow-600 text-white scale-110'
                        : 'hover:bg-white/10'
                    }`}
                  >
                    A{index === 0 ? '⁻' : index === 2 ? '⁺' : ''}
                  </button>
                ))}
              </div>

              {/* Theme Selector - Commented out
              <div className="w-px h-4 bg-white/20"></div>
              <div className="relative">
                <button
                  onClick={() => setIsThemeDropdownOpen(!isThemeDropdownOpen)}
                  className="flex items-center space-x-1 hover:bg-white/10 px-2 py-1 rounded transition-colors cursor-pointer"
                >
                  <i className="ri-palette-line"></i>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </button>

                {isThemeDropdownOpen && (
                  <div className="absolute top-full right-0 mt-1 w-48 bg-white rounded-lg shadow-xl border z-50 animate-in slide-in-from-top-2 duration-300">
                    {themes.map((themeOption) => (
                      <button
                        key={themeOption.id}
                        onClick={() => {
                          setTheme(themeOption.id);
                          setIsThemeDropdownOpen(false);
                        }}
                        className={`w-full flex items-center px-3 py-2 text-gray-700 hover:bg-orange-50 transition-colors cursor-pointer focus:outline-none focus:ring-0 ${
                          theme === themeOption.id ? 'bg-orange-100 text-orange-600' : ''
                        }`}
                      >
                        <i className={`ri-checkbox-blank-circle-fill mr-3 ${themeOption.preview}`}></i>
                        <span className="text-sm">{themeOption.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              */}

              {/* Accessibility - Commented out
              <button
                className="hover:bg-white/10 p-1 rounded transition-colors cursor-pointer"
                title="Accessibility Options"
              >
                <i className="ri-accessibility-line"></i>
              </button>
              */}
            </div>
          </div>
        </div>

        {/* Main Header with Bridge Backdrop */}
        <div className="relative bg-white">
          {/* Bridge Background Image - More visible */}
          <div
            className="absolute inset-0 bg-cover bg-bottom bg-no-repeat"
            style={{
              backgroundImage: `url('/assets/img/home/rjybanner.jpg')`,
              opacity:1
            }}
          ></div>
          {/* Subtle Orange/Yellow Overlay for text readability */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to right, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 5%, rgb(255 230 204 / 89%) 50%, rgba(255, 255, 255, 0.9) 100%, rgba(255, 255, 255, 0.95) 100%)'
            }}
          ></div>

          <div className="max-w-7xl mx-auto px-1.5 sm:px-3 lg:px-4 py-1.5 lg:py-3 flex flex-wrap lg:flex-nowrap items-center justify-center lg:justify-between relative z-10 overflow-visible gap-y-1.5 gap-x-2 lg:gap-0">
            {/* Left - Logo (hidden on mobile) */}
            {/* src="https://rmc.ap.gov.in/assets/img/home/rjy.png" */}
            <div className="hidden lg:flex items-center flex-shrink-0">
              <Link to="/" className="relative rmc-logo-link" aria-label="Go to homepage">
                <img
                  src={rmcLogo}
                  alt="RMC Logo"
                  className="w-11 h-11 sm:w-12 sm:h-12 lg:w-16 lg:h-16 object-contain rounded-full border-2 border-orange-400 hover:rotate-6 hover:scale-105 transition-all duration-400 cursor-pointer shadow-md"
                />
              </Link> 
            </div>

            {/* Center - Title (full-width on mobile; absolute-centered on lg+) */}
            <div className="w-full text-center px-0.5 lg:flex-1 lg:min-w-0 lg:w-auto lg:px-0 lg:absolute lg:left-1/2 lg:top-1/2 lg:transform lg:-translate-x-1/2 lg:-translate-y-1/2">
              <h1
                className="font-extrabold uppercase leading-tight whitespace-nowrap lg:text-2xl lg:tracking-widest"
                style={{
                  background: 'linear-gradient(135deg, #0c1e4a 0%, #1e3a8a 50%, #1e40af 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontSize: 'clamp(0.7rem, 3.4vw, 1.5rem)',
                  letterSpacing: '0.02em',
                  filter: 'drop-shadow(1px 1px 2px rgba(12, 30, 74, 0.25))'
                }}
              >
                Rajamahendravaram Municipal Corporation
              </h1>
              <p
                className="text-[9px] sm:text-xs lg:text-sm font-bold tracking-wider mt-0.5 lg:mt-1 uppercase"
                style={{
                  color: '#14532d',
                  textShadow: '1px 1px 2px rgba(20, 83, 45, 0.15)'
                }}
              >
                Government of Andhra Pradesh
              </p>

              {/* Mobile-only: Logged-in user / Login button, shown below subtitle */}
              <div className="flex lg:hidden justify-center mt-1.5" style={{ zIndex: 99999 }}>
                {isLoggedIn ? (
                  <div className="relative profile-dropdown-container" style={{ zIndex: 99999 }}>
                    <button
                      ref={profileButtonMobileRef}
                      onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                      className="flex items-center gap-1.5 px-2 py-0.5 bg-transparent border border-gray-200 rounded-md max-w-[260px]"
                      aria-label={`Logged in as ${userName}`}
                    >
                      <div className="relative flex-shrink-0">
                        <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold text-[11px]">
                          {userName.charAt(0).toUpperCase()}
                        </div>
                        <div className="absolute -bottom-0 -right-0 w-2 h-2 bg-green-500 rounded-full border border-white"></div>
                      </div>
                      <div className="text-left min-w-0">
                        <div className="text-[11px] font-medium text-gray-700 flex items-center gap-1 capitalize leading-tight">
                          <span className="truncate">{userName}</span>
                          <i className="ri-verified-badge-fill text-orange-400 text-[9px] flex-shrink-0"></i>
                        </div>
                        <div className="text-[9px] text-gray-500 flex items-center gap-1 leading-tight">
                          <i className="ri-id-card-line text-gray-400 flex-shrink-0"></i>
                          <span className="truncate">{citizenId}</span>
                        </div>
                      </div>
                      <i className={`ri-arrow-down-s-line text-gray-400 text-xs flex-shrink-0 transition-transform duration-300 ${isProfileDropdownOpen ? 'rotate-180' : ''}`}></i>
                    </button>
                  </div>
                ) : (
                  !location.pathname.includes('/citizen-login') && (
                    <div className="relative login-dropdown-container" style={{ zIndex: 99999 }}>
                      <button
                        ref={loginButtonMobileRef}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsLoginDropdownOpen(!isLoginDropdownOpen);
                        }}
                        className="flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-semibold rounded-lg shadow-md"
                      >
                        <i className="ri-login-box-line"></i>
                        <span>{currentLanguage === 'en' ? 'Login' : 'లాగిన్'}</span>
                        <i className={`ri-arrow-down-s-line transition-transform duration-300 ${isLoginDropdownOpen ? 'rotate-180' : ''}`}></i>
                      </button>
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Right - Action Buttons (hidden on mobile, shown on lg+) */}
            <div className="hidden lg:flex items-center gap-2 lg:gap-4 overflow-visible flex-shrink-0 ml-auto" style={{ zIndex: 99999 }}>
              {isLoggedIn ? (
                /* Enhanced User Profile Card */
                <div className="relative profile-dropdown-container" style={{ zIndex: 99999 }}>
                  <button
                    ref={profileButtonRef}
                    onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                    className="flex items-center gap-2 sm:gap-3 px-2 py-1.5 sm:px-4 sm:py-2.5 bg-gradient-to-r from-white to-orange-50 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group border-2 border-orange-200 hover:border-orange-400 rounded-xl max-w-[180px] sm:max-w-none"
                    aria-label={`Logged in as ${userName}`}
                  >
                    {/* Enhanced Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-lg ring-2 ring-white">
                        {userName.charAt(0).toUpperCase()}
                      </div>
                      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 sm:w-3.5 sm:h-3.5 bg-green-500 rounded-full border-2 border-white"></div>
                    </div>
                    {/* User Info - shown on all sizes, compact on mobile */}
                    <div className="text-left min-w-0">
                      <div className="text-[11px] sm:text-sm font-bold text-gray-800 flex items-center gap-1 capitalize truncate leading-tight">
                        <span className="truncate">{userName}</span>
                        <i className="ri-verified-badge-fill text-orange-500 text-[10px] sm:text-xs flex-shrink-0"></i>
                      </div>
                      <div className="text-[9px] sm:text-xs text-gray-500 flex items-center gap-1 leading-tight">
                        <i className="ri-id-card-line text-orange-400 flex-shrink-0"></i>
                        <span className="font-medium truncate">{citizenId}</span>
                      </div>
                    </div>
                    {/* Dropdown Arrow */}
                    <div className={`ml-0.5 sm:ml-1 flex-shrink-0 transition-transform duration-300 ${isProfileDropdownOpen ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-gray-400 group-hover:text-orange-500 text-sm sm:text-base"></i>
                    </div>
                  </button>

                  {/* Dropdown Menu - Rendered via Portal to appear above everything */}
                  {isProfileDropdownOpen && createPortal(
                    <div
                      className="fixed min-w-[240px] bg-white rounded-xl shadow-2xl border-2 border-orange-200 profile-dropdown-container"
                      style={{
                        zIndex: 999999,
                        top: `${dropdownPosition.top}px`,
                        right: `${dropdownPosition.right}px`,
                        minWidth: `${Math.max(dropdownPosition.width, 240)}px`
                      }}
                    >
                      {/* Menu Items */}
                      <div className="py-2">
                        <button
                          onClick={() => { navigate('/citizen-dashboard-two'); setIsProfileDropdownOpen(false); }}
                          className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 transition-all duration-200 flex items-center gap-3 focus:outline-none"
                        >
                          <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                            <i className="ri-dashboard-3-line text-indigo-600 text-lg"></i>
                          </div>
                          <span className="font-semibold text-gray-700">{currentLanguage === 'en' ? 'Dashboard' : 'డాష్‌బోర్డ్'}</span>
                        </button>
                        <button
                          onClick={handleProfileClick}
                          className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 transition-all duration-200 flex items-center gap-3 focus:outline-none"
                        >
                          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
                            <i className="ri-user-line text-orange-600 text-lg"></i>
                          </div>
                          <span className="font-semibold text-gray-700">{currentLanguage === 'en' ? 'My Profile' : 'నా ప్రొఫైల్'}</span>
                        </button>
                      </div>

                      {/* Logout Section */}
                      <div className="border-t border-gray-200 px-3 py-2">
                        <button
                          onClick={handleLogout}
                          className="w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-all duration-200 flex items-center justify-center gap-2 focus:outline-none rounded-lg font-semibold"
                        >
                          <i className="ri-logout-box-r-line text-lg"></i>
                          {currentLanguage === 'en' ? 'Logout' : 'లాగ్అవుట్'}
                        </button>
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
              ) : (
                /* Login Dropdown - Hide on login page */
                !location.pathname.includes('/citizen-login') && (
                  <div
                    className="relative login-dropdown-container pr-2"
                    style={{ zIndex: 99999 }}
                  >
                    <button
                      ref={loginButtonRef}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Calculate position before showing dropdown
                        if (loginButtonRef.current) {
                          const rect = loginButtonRef.current.getBoundingClientRect();
                          setLoginDropdownPosition({
                            top: rect.bottom + 4,
                            right: window.innerWidth - rect.right,
                            width: rect.width
                          });
                        }
                        setIsLoginDropdownOpen(!isLoginDropdownOpen);
                      }}
                      className="flex items-center gap-1 lg:gap-2 px-3 lg:px-6 py-2 lg:py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 hover:scale-102 hover:shadow-lg transition-all duration-300 text-white text-xs lg:text-sm font-semibold rounded-lg shadow-md cursor-pointer"
                    >
                      <i className="ri-login-box-line"></i>
                      <span>{currentLanguage === 'en' ? 'Login' : 'లాగిన్'}</span>
                      <i className={`ri-arrow-down-s-line transition-transform duration-300 ${isLoginDropdownOpen ? 'rotate-180' : ''}`}></i>
                    </button>

                    {/* Login Dropdown Menu - Click only, no hover */}
                    {isLoginDropdownOpen && createPortal(
                      <div
                        className="fixed min-w-[200px] bg-white rounded-xl shadow-2xl border-2 border-orange-200 login-dropdown-container overflow-hidden"
                        style={{
                          zIndex: 999999,
                          top: `${loginDropdownPosition.top}px`,
                          right: `${loginDropdownPosition.right}px`,
                          minWidth: `${Math.max(loginDropdownPosition.width, 200)}px`
                        }}
                      >
                        <div className="py-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCitizenLogin(); setIsLoginDropdownOpen(false); }}
                            className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 transition-all duration-200 flex items-center gap-3 focus:outline-none"
                          >
                            <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
                              <i className="ri-user-line text-orange-600 text-lg"></i>
                            </div>
                            <span className="font-semibold text-gray-700">{currentLanguage === 'en' ? 'Citizen Login' : 'సిటిజన్ లాగిన్'}</span>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); window.open('https://rmc.ap.gov.in/internal', '_blank'); setIsLoginDropdownOpen(false); }}
                            className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 transition-all duration-200 flex items-center gap-3 focus:outline-none"
                          >
                            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
                              <i className="ri-building-line text-blue-600 text-lg"></i>
                            </div>
                            <span className="font-semibold text-gray-700">{currentLanguage === 'en' ? 'Employee Login' : 'ఉద్యోగి లాగిన్'}</span>
                          </button>
                        </div>
                      </div>,
                      document.body
                    )}
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* Navigation Menu - White Background */}
        <nav className="bg-white border-t border-b border-gray-200 h-12 relative z-[50] shadow-sm">
          <div className="max-w-7xl mx-auto px-3 lg:px-4">
            {/* Mobile Hamburger Button */}
            <div className="flex lg:hidden items-center justify-between h-12">
              <span className="text-sm font-medium text-gray-700">
                {currentLanguage === 'en' ? 'Menu' : 'మెనూ'}
              </span>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer"
                aria-label="Toggle menu"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center justify-center space-x-1 h-12">
              <Link
                to="/"
                className={`relative flex items-center transition-all duration-300 cursor-pointer font-medium text-sm px-4 py-2 rounded-lg group whitespace-nowrap ${
                  location.pathname === '/' ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                }`}
              >
                <i className="ri-home-line mr-2 transition-transform duration-300 group-hover:scale-110"></i>
                Home
              </Link>

              {/* About RMC Dropdown - Hover to open */}
              <div
                className="relative h-full flex items-center"
                onMouseEnter={() => setIsAboutDropdownOpen(true)}
                onMouseLeave={() => setIsAboutDropdownOpen(false)}
              >
                <button
                  className={`relative flex items-center transition-all duration-300 cursor-pointer font-medium text-sm px-4 py-2 rounded-lg group h-full whitespace-nowrap ${
                    location.pathname.includes('/about') || isAboutDropdownOpen ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  About RMC
                  <i className={`ri-arrow-down-s-line ml-1 transition-transform duration-300 ${
                    isAboutDropdownOpen ? 'rotate-180' : ''
                  }`}></i>
                </button>

                {isAboutDropdownOpen && (
                  <div className="absolute top-full left-0 mt-0 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-50 animate-in slide-in-from-top-2 duration-300">
                    <div className="py-2">
                      {aboutMenuItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          className="flex items-center px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-all duration-300 group focus:outline-none focus:ring-0"
                        >
                          <i className={`${item.icon} mr-3 text-orange-500 transition-transform duration-300 group-hover:rotate-12`}></i>
                          <span className="text-sm font-medium">{currentLanguage === 'en' ? item.titleEn : item.titleTe}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/services"
                className={`relative flex items-center transition-all duration-300 cursor-pointer font-medium text-sm px-4 py-2 rounded-lg group ${
                  location.pathname.includes('/services') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                }`}
              >
                <i className="ri-customer-service-2-line mr-2 transition-transform duration-300 group-hover:scale-110"></i>
                Citizen Services
              </Link>

              {/* Tourism Dropdown - Hover to open */}
              <div
                className="relative h-full flex items-center"
                onMouseEnter={() => setIsTourismDropdownOpen(true)}
                onMouseLeave={() => setIsTourismDropdownOpen(false)}
              >
                <button
                  className={`relative flex items-center transition-all duration-300 cursor-pointer font-medium text-sm px-4 py-2 rounded-lg group h-full whitespace-nowrap ${
                    location.pathname === '/tourism' || isTourismDropdownOpen ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  <i className="ri-map-2-line mr-2 transition-transform duration-300 group-hover:scale-110"></i>
                  Tourism
                  <i className={`ri-arrow-down-s-line ml-1 transition-transform duration-300 ${
                    isTourismDropdownOpen ? 'rotate-180' : ''
                  }`}></i>
                </button>

                {isTourismDropdownOpen && (
                  <div className="absolute top-full left-0 mt-0 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50 animate-in slide-in-from-top-2 duration-300">
                    <div className="py-2">
                      {tourismMenuItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          className="flex items-center px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-all duration-300 group focus:outline-none focus:ring-0"
                        >
                          <i className={`${item.icon} mr-3 text-orange-500 transition-transform duration-300 group-hover:rotate-12`}></i>
                          <span className="text-sm font-medium whitespace-nowrap">{currentLanguage === 'en' ? item.titleEn : item.titleTe}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Dashboards Dropdown - Hover to open */}
              <div
                className="relative h-full flex items-center"
                onMouseEnter={() => setIsDashboardsDropdownOpen(true)}
                onMouseLeave={() => setIsDashboardsDropdownOpen(false)}
              >
                <button
                  className={`relative flex items-center transition-all duration-300 cursor-pointer font-medium text-sm px-4 py-2 rounded-lg group h-full whitespace-nowrap ${
                    location.pathname.includes('/dashboards') || isDashboardsDropdownOpen ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  <i className="ri-dashboard-line mr-2 transition-transform duration-300 group-hover:scale-110"></i>
                  Dashboards
                  <i className={`ri-arrow-down-s-line ml-1 transition-transform duration-300 ${
                    isDashboardsDropdownOpen ? 'rotate-180' : ''
                  }`}></i>
                </button>

                {isDashboardsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-0 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50 animate-in slide-in-from-top-2 duration-300">
                    <div className="py-2">
                      {dashboardMenuItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          className="flex items-center px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-all duration-300 group focus:outline-none focus:ring-0"
                        >
                          <i className={`${item.icon} mr-3 text-orange-500 transition-transform duration-300 group-hover:rotate-12`}></i>
                          <span className="text-sm font-medium whitespace-nowrap">{currentLanguage === 'en' ? item.titleEn : item.titleTe}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Resources Dropdown - Hover to open */}
              <div
                className="relative h-full flex items-center"
                onMouseEnter={() => setIsResourcesDropdownOpen(true)}
                onMouseLeave={() => setIsResourcesDropdownOpen(false)}
              >
                <button
                  className={`relative flex items-center transition-all duration-300 cursor-pointer font-medium text-sm px-4 py-2 rounded-lg group h-full whitespace-nowrap ${
                    location.pathname.includes('/resources') || isResourcesDropdownOpen ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  <i className="ri-folder-line mr-2 transition-transform duration-300 group-hover:scale-110"></i>
                  Resources
                  <i className={`ri-arrow-down-s-line ml-1 transition-transform duration-300 ${
                    isResourcesDropdownOpen ? 'rotate-180' : ''
                  }`}></i>
                </button>

                {isResourcesDropdownOpen && (
                  <div className="absolute top-full left-0 mt-0 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50 animate-in slide-in-from-top-2 duration-300">
                    <div className="py-2">
                      {resourcesMenuItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          className="flex items-center px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-all duration-300 group focus:outline-none focus:ring-0"
                        >
                          <i className={`${item.icon} mr-3 text-orange-500 transition-transform duration-300 group-hover:rotate-12`}></i>
                          <span className="text-sm font-medium whitespace-nowrap">{currentLanguage === 'en' ? item.titleEn : item.titleTe}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/feedback"
                className={`relative flex items-center transition-all duration-300 cursor-pointer font-medium text-sm px-4 py-2 rounded-lg group whitespace-nowrap ${
                  location.pathname === '/feedback' ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                }`}
              >
                <i className="ri-feedback-line mr-2 transition-transform duration-300 group-hover:scale-110"></i>
                Feedback
              </Link>

              <Link
                to="/web/contacts/"
                className={`relative flex items-center transition-all duration-300 cursor-pointer font-medium text-sm px-4 py-2 rounded-lg group whitespace-nowrap ${
                  location.pathname === '/web/contacts/' || location.pathname.startsWith('/web/contacts') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                }`}
              >
                <i className="ri-mail-line mr-2 transition-transform duration-300 group-hover:scale-110"></i>
                Contact Us
              </Link>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg max-h-[70vh] overflow-y-auto">
              <div className="py-2 px-3">
                {/* Home */}
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/' ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  <i className="ri-home-line mr-3 text-lg"></i>
                  Home
                </Link>

                {/* About RMC - Expandable */}
                <div>
                  <button
                    onClick={() => setIsAboutDropdownOpen(!isAboutDropdownOpen)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      location.pathname.includes('/about') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                    }`}
                  >
                    <span className="flex items-center">
                      <i className="ri-building-line mr-3 text-lg"></i>
                      About RMC
                    </span>
                    <i className={`ri-arrow-down-s-line transition-transform duration-300 ${isAboutDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  {isAboutDropdownOpen && (
                    <div className="ml-6 border-l-2 border-orange-200 pl-2 mb-1">
                      {aboutMenuItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center px-4 py-2.5 text-sm text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                        >
                          <i className={`${item.icon} mr-3 text-orange-400`}></i>
                          {currentLanguage === 'en' ? item.titleEn : item.titleTe}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Citizen Services */}
                <Link
                  to="/services"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname.includes('/services') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  <i className="ri-customer-service-2-line mr-3 text-lg"></i>
                  Citizen Services
                </Link>

                {/* Tourism - Expandable */}
                <div>
                  <button
                    onClick={() => setIsTourismDropdownOpen(!isTourismDropdownOpen)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      location.pathname.includes('/tourism') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                    }`}
                  >
                    <span className="flex items-center">
                      <i className="ri-map-2-line mr-3 text-lg"></i>
                      Tourism
                    </span>
                    <i className={`ri-arrow-down-s-line transition-transform duration-300 ${isTourismDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  {isTourismDropdownOpen && (
                    <div className="ml-6 border-l-2 border-orange-200 pl-2 mb-1">
                      {tourismMenuItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center px-4 py-2.5 text-sm text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                        >
                          <i className={`${item.icon} mr-3 text-orange-400`}></i>
                          {currentLanguage === 'en' ? item.titleEn : item.titleTe}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Dashboards - Expandable */}
                <div>
                  <button
                    onClick={() => setIsDashboardsDropdownOpen(!isDashboardsDropdownOpen)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      location.pathname.includes('/dashboards') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                    }`}
                  >
                    <span className="flex items-center">
                      <i className="ri-dashboard-line mr-3 text-lg"></i>
                      Dashboards
                    </span>
                    <i className={`ri-arrow-down-s-line transition-transform duration-300 ${isDashboardsDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  {isDashboardsDropdownOpen && (
                    <div className="ml-6 border-l-2 border-orange-200 pl-2 mb-1">
                      {dashboardMenuItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center px-4 py-2.5 text-sm text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                        >
                          <i className={`${item.icon} mr-3 text-orange-400`}></i>
                          {currentLanguage === 'en' ? item.titleEn : item.titleTe}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Resources - Expandable */}
                <div>
                  <button
                    onClick={() => setIsResourcesDropdownOpen(!isResourcesDropdownOpen)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      location.pathname.includes('/resources') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                    }`}
                  >
                    <span className="flex items-center">
                      <i className="ri-folder-line mr-3 text-lg"></i>
                      Resources
                    </span>
                    <i className={`ri-arrow-down-s-line transition-transform duration-300 ${isResourcesDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  {isResourcesDropdownOpen && (
                    <div className="ml-6 border-l-2 border-orange-200 pl-2 mb-1">
                      {resourcesMenuItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center px-4 py-2.5 text-sm text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                        >
                          <i className={`${item.icon} mr-3 text-orange-400`}></i>
                          {currentLanguage === 'en' ? item.titleEn : item.titleTe}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Feedback / Suggestions */}
                <Link
                  to="/feedback"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/feedback' ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  <i className="ri-feedback-line mr-3 text-lg"></i>
                  Feedback / Suggestions
                </Link>

                {/* Contact Us */}
                <Link
                  to="/web/contacts/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/web/contacts') ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  <i className="ri-mail-line mr-3 text-lg"></i>
                  Contact Us
                </Link>
              </div>
            </div>
          )}
        </nav>
      </header>
    </>
  );
}
